import { useMemo } from 'react'
import { GetStaticProps, GetStaticPropsContext } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'

import HorizontalMenu from '~/components/HorizontalMenu/HorizontalMenu'
import VerticalMenu from '~/components/VerticalMenu/VerticalMenu'
import { SiteMapDataModel } from '~/math-vault/types/sanityModel.types'
import { ClearLayout } from '~/shared/layouts/ClearLayout'
import { usePageTitle } from '~/utils/tags'
import { AppLanguage } from '~/types/locale'
import { mathVaultSanity } from '~/shared/sanity/math-vault'
import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES, COMMON_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import Metatags from '~/components/Metadata/Metatags'

const IconData: { [key: string]: any } = {
  'numbers-and-quantities': {
    src: 'icons/theory/measurement-icon.svg',
    alt: 'NumbersAndQuantitiesIcon',
  },
  geometry: {
    src: 'icons/theory/geometry-icon.svg',
    alt: 'GeometryIcon',
  },
  statistics: {
    src: 'icons/theory/dice-icon.svg',
    alt: 'StatisticsIcon',
  },

  'tall-og-maling': {
    src: 'icons/theory/measurement-icon.svg',
    alt: 'NumbersAndQuantitiesIcon',
  },
  geometri: {
    src: 'icons/theory/geometry-icon.svg',
    alt: 'GeometryIcon',
  },
  statistikk: {
    src: 'icons/theory/dice-icon.svg',
    alt: 'StatisticsIcon',
  },
}

const ColorClasses: { [key: string]: any } = {
  'numbers-and-quantities': {
    bg: 'bg-theory-red-light',
    text: 'text-theory-red',
  },
  geometry: {
    bg: 'bg-theory-magenta-light',
    text: 'text-theory-magenta',
  },
  statistics: {
    bg: 'bg-theory-purple-light',
    text: 'text-theory-purple',
  },
  'tall-og-maling': {
    bg: 'bg-theory-red-light',
    text: 'text-theory-red',
  },
  geometri: {
    bg: 'bg-theory-magenta-light',
    text: 'text-theory-magenta',
  },
  statistikk: {
    bg: 'bg-theory-purple-light',
    text: 'text-theory-purple',
  },
}

export default function MathVaultSitemap({ siteMapData }: { siteMapData: any[] }): React.ReactElement {
  const { t } = useTranslation('mathVault')

  const links = useMemo(() => {
    // elevator -> elevator level -> chests -> activity level -> activity
    const elevators: { [key: string]: any } = {}

    siteMapData.forEach((data) => {
      if (!elevators[data.elevatorKey]) {
        elevators[data.elevatorKey] = {
          key: data.elevatorKey,
          path: {
            src: data.elevatorKey,
            alt: data.elevator,
          },
          icon: IconData[data.elevatorKey],
          h1: data.elevator,
          children: [],
          activeClass: ColorClasses[data.elevatorKey],
        }
      }

      // eslint-disable-next-line fp/no-mutating-methods
      elevators[data.elevatorKey].children.push({
        key: `elevator-${data.key}`,
        path: `${data.elevatorKey}/${data.key}`,
        h1: data.name,
        activeClass: ColorClasses[data.elevatorKey],
        children: data.chests.filter(Boolean).map((chest) => ({
          key: `elevator-${data.key}-chest-${chest.serial}`,
          h1: chest.name,
          path: `${data.elevatorKey}/${data.key}/${chest.key}`,
          activeClass: ColorClasses[data.elevatorKey],
          children: Array.isArray(chest.tabs)
            ? chest.tabs.filter(Boolean).map((level) => ({
                key: `tabs-${level.serial}`,
                h1: `Level ${level.serial}`,
                path: `${data.elevatorKey}/${data.key}/${chest.key}/${level.serial}`,
                activeClass: ColorClasses[data.elevatorKey],
                children: Array.isArray(level.activities)
                  ? level.activities.map((activity) => ({
                      key: `level-${activity.serial}`,
                      h1: `${t('activity')} ${activity.serial}`,
                      path: `${data.elevatorKey}/${data.key}/${chest.key}/${level.serial}/${activity.serial}`,
                      activeClass: ColorClasses[data.elevatorKey],
                    }))
                  : [],
              }))
            : [],
        })),
      })
    })

    const rootItemKeys = Object.keys(elevators)
    return rootItemKeys.map((key) => elevators[key])
  }, [siteMapData, t])

  return (
    <div>
      <Metatags metaDescription={t('metaDescription')} title={usePageTitle(t('title'))} />

      <ClearLayout>
        <div className="h-full lg:h-screen">
          <div className="hidden lg:flex flex-col items-center pt-6">
            <HorizontalMenu links={links} title={t('siteMapH1')} />
          </div>

          <div className="lg:hidden relative md:grid md:grid-cols-2 md:gap-x-8 justify-between items-start">
            <VerticalMenu items={links} title={t('siteMapH1')} />
          </div>
        </div>
      </ClearLayout>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }: GetStaticPropsContext<{ locale: AppLanguage }>) => {
  const siteMapData: SiteMapDataModel[] = await mathVaultSanity.getChestDataForSiteMap(locale as AppLanguage)

  return {
    props: {
      siteMapData,
      ...(await serverSideTranslations(locale as string, [
        ...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES,
        ...COMMON_TRANSLATION_MODULE_NAMES,
        'mathVault',
      ])),
    },
  }
}
